import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { depensesAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Check, Clock, Edit, CreditCard, Download, Euro, Camera } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
  'Loyer',
  'Internet',
  'Téléphone',
  'Matériel',
  'Petit outillage',
  'Fournitures',
  'Bureau',
  'Carburant',
  'Péages',
  'Hôtel',
  'Restaurant',
  'Logiciels - Comptant',
  'Logiciels - Abonnement',
  'Formation',
  'Sous-traitance',
  'Assurance',
  'Frais bancaires',
  'Autre'
];

const CONDITIONS_PAIEMENT = [
  { value: 'comptant', label: 'Comptant' },
  { value: '30j_fin_mois', label: '30 jours fin de mois' },
  { value: '60j_fin_mois', label: '60 jours fin de mois' },
  { value: 'acompte', label: 'Paiement par acomptes' }
];

const COLORS = ['#2A9D8F', '#E9C46A', '#264653', '#F4A261', '#E76F51', '#457B9D', '#A8DADC', '#1D3557', '#F1FAEE', '#B5838D'];

const DepensesPage = () => {
  const [depenses, setDepenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paiementDialogOpen, setPaiementDialogOpen] = useState(false);
  const [selectedDepense, setSelectedDepense] = useState(null);
  const [paiementAmount, setPaiementAmount] = useState('');
  const [paiementNote, setPaiementNote] = useState('');
  const [formData, setFormData] = useState({
    description: '',
    montant_ttc: '',
    tva_rate: '20',
    categorie: '',
    fournisseur: '',
    date: new Date().toISOString().split('T')[0],
    statut: 'en_attente',
    condition_paiement: 'comptant'
  });

  const loadDepenses = useCallback(async () => {
    try {
      const response = await depensesAPI.getAll();
      setDepenses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepenses();
  }, [loadDepenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await depensesAPI.create({
        ...formData,
        montant_ttc: parseFloat(formData.montant_ttc),
        tva_rate: parseFloat(formData.tva_rate),
        date: new Date(formData.date).toISOString()
      });
      toast.success('Dépense ajoutée');
      setDialogOpen(false);
      resetForm();
      loadDepenses();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedDepense) return;
    
    try {
      await depensesAPI.update(selectedDepense.id, {
        description: formData.description,
        montant_ttc: parseFloat(formData.montant_ttc),
        tva_rate: parseFloat(formData.tva_rate),
        categorie: formData.categorie,
        fournisseur: formData.fournisseur || null,
        date: new Date(formData.date).toISOString(),
        statut: formData.statut
      });
      toast.success('Dépense modifiée');
      setEditDialogOpen(false);
      resetForm();
      loadDepenses();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const openEditDialog = (depense) => {
    setSelectedDepense(depense);
    setFormData({
      description: depense.description,
      montant_ttc: depense.montant_ttc.toString(),
      tva_rate: depense.tva_rate.toString(),
      categorie: depense.categorie,
      fournisseur: depense.fournisseur || '',
      date: depense.date ? depense.date.split('T')[0] : new Date().toISOString().split('T')[0],
      statut: depense.statut || 'en_attente'
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      description: '',
      montant_ttc: '',
      tva_rate: '20',
      categorie: '',
      fournisseur: '',
      date: new Date().toISOString().split('T')[0],
      statut: 'en_attente',
      condition_paiement: 'comptant'
    });
    setSelectedDepense(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await depensesAPI.delete(id);
      toast.success('Dépense supprimée');
      loadDepenses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleStatut = async (id, currentStatut) => {
    const newStatut = currentStatut === 'payee' ? 'en_attente' : 'payee';
    try {
      await depensesAPI.updateStatut(id, newStatut);
      toast.success(newStatut === 'payee' ? 'Dépense marquée payée' : 'Dépense marquée en attente');
      loadDepenses();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openPaiementDialog = (depense) => {
    setSelectedDepense(depense);
    setPaiementAmount('');
    setPaiementNote('');
    setPaiementDialogOpen(true);
  };

  const handleAddPaiement = async () => {
    if (!selectedDepense || !paiementAmount) return;
    
    try {
      await depensesAPI.addPaiement(selectedDepense.id, {
        montant: parseFloat(paiementAmount),
        note: paiementNote || null
      });
      
      const newMontantPaye = (selectedDepense.montant_paye || 0) + parseFloat(paiementAmount);
      
      if (newMontantPaye >= selectedDepense.montant_ttc) {
        toast.success('Dépense entièrement payée !');
      } else {
        toast.success(`Paiement de ${formatCurrency(parseFloat(paiementAmount))} enregistré`);
      }
      
      setPaiementDialogOpen(false);
      setPaiementAmount('');
      setPaiementNote('');
      setSelectedDepense(null);
      loadDepenses();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement du paiement');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getConditionLabel = (condition) => {
    const found = CONDITIONS_PAIEMENT.find(c => c.value === condition);
    return found ? found.label : condition;
  };

  const filteredDepenses = depenses.filter((d) => {
    const matchSearch = d.description.toLowerCase().includes(search.toLowerCase()) ||
                       (d.fournisseur && d.fournisseur.toLowerCase().includes(search.toLowerCase()));
    const matchCategory = categoryFilter === 'all' || d.categorie === categoryFilter;
    return matchSearch && matchCategory;
  });

  // Group by category for chart
  const categoryData = CATEGORIES.map((cat, index) => ({
    name: cat,
    value: filteredDepenses.filter(d => d.categorie === cat).reduce((sum, d) => sum + d.montant_ttc, 0),
    color: COLORS[index]
  })).filter(d => d.value > 0);

  const totals = {
    ttc: filteredDepenses.reduce((sum, d) => sum + d.montant_ttc, 0),
    tva: filteredDepenses.reduce((sum, d) => sum + d.tva_deductible, 0)
  };

  return (
    <div className="space-y-6" data-testid="depenses-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Dépenses</h1>
          <p className="text-muted-foreground">Suivez vos dépenses professionnelles</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 gap-2"
              onClick={() => navigate('/scan-facture')}
              data-testid="scan-facture-btn"
            >
              <Camera className="h-4 w-4" />
              📸 Scanner une facture
            </Button>
            <DialogTrigger asChild>
              <Button data-testid="new-depense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle dépense
              </Button>
            </DialogTrigger>
          </div>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter une dépense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la dépense"
                  required
                  data-testid="depense-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={(value) => setFormData({ ...formData, categorie: value })}
                  required
                >
                  <SelectTrigger data-testid="depense-categorie">
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant TTC (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.montant_ttc}
                    onChange={(e) => setFormData({ ...formData, montant_ttc: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="depense-montant"
                  />
                </div>
                <div className="space-y-2">
                  <Label>TVA (%)</Label>
                  <Select
                    value={formData.tva_rate}
                    onValueChange={(value) => setFormData({ ...formData, tva_rate: value })}
                  >
                    <SelectTrigger data-testid="depense-tva">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5.5">5.5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fournisseur (optionnel)</Label>
                <Input
                  value={formData.fournisseur}
                  onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                  placeholder="Nom du fournisseur"
                  data-testid="depense-fournisseur"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="depense-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select
                    value={formData.statut}
                    onValueChange={(value) => setFormData({ ...formData, statut: value })}
                  >
                    <SelectTrigger data-testid="depense-statut">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en_attente">En attente</SelectItem>
                      <SelectItem value="payee">Payée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Condition de paiement</Label>
                <Select
                  value={formData.condition_paiement}
                  onValueChange={(value) => setFormData({ ...formData, condition_paiement: value })}
                >
                  <SelectTrigger data-testid="depense-condition">
                    <SelectValue placeholder="Condition de paiement" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITIONS_PAIEMENT.map((cond) => (
                      <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="depense-submit">
                Ajouter la dépense
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog Modifier Dépense */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la dépense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la dépense"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select
                value={formData.categorie}
                onValueChange={(value) => setFormData({ ...formData, categorie: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant TTC (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant_ttc}
                  onChange={(e) => setFormData({ ...formData, montant_ttc: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>TVA (%)</Label>
                <Select
                  value={formData.tva_rate}
                  onValueChange={(value) => setFormData({ ...formData, tva_rate: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5.5">5.5%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="20">20%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fournisseur (optionnel)</Label>
              <Input
                value={formData.fournisseur}
                onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                placeholder="Nom du fournisseur"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={formData.statut}
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_attente">En attente</SelectItem>
                    <SelectItem value="acompte">Acompte</SelectItem>
                    <SelectItem value="payee">Payée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full">
              Enregistrer les modifications
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Paiement Fournisseur */}
      <Dialog open={paiementDialogOpen} onOpenChange={setPaiementDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
          </DialogHeader>
          {selectedDepense && (
            <div className="space-y-4">
              <div className="bg-rose-50 p-4 rounded-lg space-y-2">
                <p className="font-medium">{selectedDepense.fournisseur || 'Fournisseur'}</p>
                <p className="text-sm text-muted-foreground">{selectedDepense.description}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Montant TTC</p>
                    <p className="font-bold">{formatCurrency(selectedDepense.montant_ttc)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Déjà payé</p>
                    <p className="font-bold text-blue-600">{formatCurrency(selectedDepense.montant_paye || 0)}</p>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">Reste à payer</p>
                  <p className="text-xl font-bold text-rose-600">{formatCurrency(selectedDepense.reste_a_payer || selectedDepense.montant_ttc)}</p>
                </div>
                <div className="text-xs text-gray-500">
                  Condition : {getConditionLabel(selectedDepense.condition_paiement)}
                </div>
              </div>
              
              {/* Historique des paiements */}
              {selectedDepense.paiements && selectedDepense.paiements.length > 0 && (
                <div className="border rounded-lg p-3">
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Historique des paiements ({selectedDepense.paiements.length})
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedDepense.paiements.map((p, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm bg-green-50 p-2 rounded">
                        <div>
                          <span className="text-gray-600">{formatDate(p.date)}</span>
                          {p.note && <span className="text-gray-500 ml-2">- {p.note}</span>}
                        </div>
                        <span className="font-semibold text-green-700">+{formatCurrency(p.montant)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Montant du paiement (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paiementAmount}
                  onChange={(e) => setPaiementAmount(e.target.value)}
                  placeholder="0.00"
                  max={selectedDepense.reste_a_payer || selectedDepense.montant_ttc}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Note (optionnel)</Label>
                <Input
                  value={paiementNote}
                  onChange={(e) => setPaiementNote(e.target.value)}
                  placeholder="Ex: Virement, Chèque n°123..."
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleAddPaiement} 
                  className="flex-1"
                  disabled={!paiementAmount || parseFloat(paiementAmount) <= 0}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Enregistrer le paiement
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setPaiementAmount((selectedDepense.reste_a_payer || selectedDepense.montant_ttc).toString());
                  }}
                >
                  Solder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats & Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Total dépenses TTC</p>
              <p className="text-3xl font-bold font-['Manrope']">{formatCurrency(totals.ttc)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">TVA déductible</p>
              <p className="text-2xl font-bold font-['Manrope'] text-primary">{formatCurrency(totals.tva)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Nombre de dépenses</p>
              <p className="text-2xl font-bold font-['Manrope']">{filteredDepenses.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Répartition par catégorie</h3>
            {categoryData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Aucune dépense à afficher
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-depenses"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-category">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Fournisseur</TableHead>
                <TableHead>HT</TableHead>
                <TableHead>TVA</TableHead>
                <TableHead>TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredDepenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Aucune dépense trouvée
                  </TableCell>
                </TableRow>
              ) : (
                filteredDepenses.map((d) => (
                  <TableRow key={d.id} className="table-row-hover">
                    <TableCell>{formatDate(d.date)}</TableCell>
                    <TableCell>{d.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{d.categorie}</Badge>
                    </TableCell>
                    <TableCell>{d.fournisseur || '-'}</TableCell>
                    <TableCell>{formatCurrency(d.montant_ht)}</TableCell>
                    <TableCell>{formatCurrency(d.tva_deductible)}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(d.montant_ttc)}</TableCell>
                    <TableCell>
                      {d.statut === 'payee' ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Payée</Badge>
                      ) : d.statut === 'acompte' ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Acompte</Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">En attente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(d)}
                          title="Modifier"
                          data-testid={`edit-depense-${d.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {d.statut !== 'payee' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPaiementDialog(d)}
                              title="Enregistrer paiement"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <CreditCard className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatut(d.id, d.statut)}
                              title="Marquer payée"
                              className="text-green-600 hover:text-green-700"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => depensesAPI.downloadPDF(d.id)}
                          className="h-8 w-8 p-0"
                          title="Télécharger PDF"
                        >
                          <Download className="h-4 w-4 text-gray-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(d.id)}
                          className="text-destructive hover:text-destructive"
                          title="Supprimer"
                          data-testid={`delete-depense-${d.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DepensesPage;
